import { useState, useEffect } from 'react';
import { Menu, X, Code, Smartphone, Search, Cloud, Palette, Target, Users, Award, Clock, CheckCircle, Star, Mail, Phone, MapPin, Briefcase, ArrowRight, Zap, Shield, TrendingUp } from 'lucide-react';
import Branches from './components/Branches';
import EnquiryPage from './components/EnquiryPage';
import bbs from "../src/assets/bbs.png"
import tg from "../src/assets/tg.png"
import ks from "../src/assets/ks.png"
import vt from "../src/assets/vtcodex.jpeg"

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [page, setPage] = useState('home'); // 'home' | 'enquiry'

  const scrollToSection = (id) => {
    if (page !== 'home') {
      setPage('home');
      setIsMenuOpen(false);
      // wait for home page to render before scrolling
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) element.scrollIntoView({ behavior: 'smooth' });
      }, 50);
      return;
    }
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  const goToEnquiry = () => {
    setPage('enquiry');
    setIsMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goHome = () => {
    setPage('home');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <Navbar
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
        scrollToSection={scrollToSection}
        goToEnquiry={goToEnquiry}
      />
      {page === 'enquiry' ? (
        <EnquiryPage goHome={goHome} />
      ) : (
        <>
          <Hero scrollToSection={scrollToSection} />
          <About />
          <Services />
          <Training />
          <WhyChooseUs />
          <Testimonials />
          <ClientsPartners />
          <Branches goToEnquiry={goToEnquiry} />
          <Careers />
          <Contact />
        </>
      )}
      <Footer scrollToSection={scrollToSection} />
    </div>
  );
}

function Navbar({ isMenuOpen, setIsMenuOpen, scrollToSection, goToEnquiry }) {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav className={`fixed top-0 w-full z-50 transition-all duration-500 ${scrolled ? 'bg-white/95 backdrop-blur-lg shadow-lg' : 'bg-white/80 backdrop-blur-sm'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <div 
            className="flex items-center space-x-3 cursor-pointer group" 
            onClick={() => scrollToSection('home')}
          >
            <div className="bg-gradient-to-br from-blue-300  to-black p-3 rounded-xl shadow-lg transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
              <Code className="h-7 w-7 text-white" />
            </div>
            <div>
              <span className="text-2xl font-bold bg-gradient-to-r from-blue-300  to-black bg-clip-text text-transparent">
                KS INFOTECH
              </span>
              <div className="h-0.5 bg-gradient-to-r from-blue-300  to-black transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
            </div>
          </div>

          <div className="hidden md:flex items-center space-x-1">
            {['Home', 'About', 'Services', 'Training', 'Branches', 'Careers'].map((item, index) => (
              <button
                key={item}
                onClick={() => scrollToSection(item.toLowerCase())}
                className="relative px-4 py-2 text-gray-700 hover:text-blue-900 transition-colors font-medium group"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {item}
                <span className="absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-blue-300  to-black transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></span>
              </button>
            ))}
            <button
              onClick={() => scrollToSection('contact')}
              className="ml-4 bg-gradient-to-r from-blue-300  to-black text-white px-6 py-2.5 rounded-full hover:shadow-xl transform hover:scale-105 transition-all duration-300 font-medium relative overflow-hidden group"
            >
              <span className="relative z-10">Contact</span>
              <div className="absolute inset-0 bg-gradient-to-r from-blue-300  to-blue-950 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
            </button>
            <button
              onClick={goToEnquiry}
              className="ml-2 bg-amber-400 text-blue-950 px-6 py-2.5 rounded-full hover:shadow-xl hover:bg-amber-300 transform hover:scale-105 transition-all duration-300 font-semibold"
            >
              Enquiry
            </button>
          </div>

          <button
            className="md:hidden text-gray-700 hover:text-blue-900 transition-colors"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
          </button>
        </div>
      </div>

      <div className={`md:hidden transition-all duration-500 ease-in-out ${isMenuOpen ? 'max-h-[32rem] opacity-100' : 'max-h-0 opacity-0'} overflow-hidden bg-white border-t`}>
        <div className="px-4 py-4 space-y-1">
          {['Home', 'About', 'Services', 'Training', 'Branches', 'Careers', 'Contact'].map((item, index) => (
            <button
              key={item}
              onClick={() => scrollToSection(item.toLowerCase())}
              className="block w-full text-left text-gray-700 hover:text-blue-900 hover:bg-blue-50 py-3 px-4 rounded-lg font-medium transition-all duration-300"
              style={{ 
                animation: isMenuOpen ? `slideIn 0.3s ease-out ${index * 50}ms both` : 'none'
              }}
            >
              {item}
            </button>
          ))}
          <button
            onClick={goToEnquiry}
            className="block w-full text-left bg-amber-400 text-blue-950 hover:bg-amber-300 py-3 px-4 rounded-lg font-semibold transition-all duration-300 mt-2"
            style={{ animation: isMenuOpen ? `slideIn 0.3s ease-out 350ms both` : 'none' }}
          >
            Enquiry
          </button>
        </div>
      </div>
    </nav>
  );
}

function Hero({ scrollToSection }) {
  const [currentWord, setCurrentWord] = useState(0);
  const words = ['Innovate', 'Build', 'Transform', 'Grow'];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentWord((prev) => (prev + 1) % words.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [words.length]);

  return (
    <section id="home" className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50  to-blue-50">
        <div className="absolute inset-0 opacity-30">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute bg-blue-400 rounded-full"
              style={{
                width: `${Math.random() * 300 + 50}px`,
                height: `${Math.random() * 300 + 50}px`,
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animation: `float ${Math.random() * 10 + 10}s ease-in-out infinite`,
                animationDelay: `${Math.random() * 5}s`,
                opacity: 0.1,
              }}
            />
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className="space-y-8 animate-fadeInUp">
            <div className="inline-block">
              <div className="flex items-center space-x-2 bg-blue-100 px-4 py-2 rounded-full animate-slideInLeft">
                <Zap className="h-4 w-4 text-blue-900" />
                <span className="text-sm font-semibold text-blue-900">Leading IT Solutions Provider</span>
              </div>
            </div>
            
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 leading-tight">
              <span className="block animate-slideInLeft" style={{ animationDelay: '100ms' }}>
                {words[currentWord]}.
              </span>
              <span className="block mt-2 animate-slideInLeft" style={{ animationDelay: '200ms' }}>
                Build. 
              </span>
              <span className="block mt-2 bg-gradient-to-r from-blue-300  to-black bg-clip-text text-transparent animate-slideInLeft" style={{ animationDelay: '300ms' }}>
                Grow.
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-600 leading-relaxed animate-slideInLeft" style={{ animationDelay: '400ms' }}>
              Empowering businesses with cutting-edge technology solutions. We transform your digital vision into reality.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 animate-slideInLeft" style={{ animationDelay: '500ms' }}>
              <button
                onClick={() => scrollToSection('services')}
                className="group bg-gradient-to-r from-blue-300  to-black text-white px-8 py-4 rounded-full hover:shadow-2xl transition-all transform hover:scale-105 font-semibold text-lg flex items-center justify-center space-x-2 relative overflow-hidden"
              >
                <span className="relative z-10">Our Services</span>
                <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform relative z-10" />
                <div className="absolute inset-0 bg-gradient-to-r from-blue-300  to-blue-950 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="bg-white text-blue-900 px-8 py-4 rounded-full border-3 border-blue-900 hover:bg-blue-50 transition-all font-bold text-lg hover:shadow-xl transform hover:scale-105"
              >
                Get Started
              </button>
            </div>

            <div className="flex items-center pt-4 animate-slideInLeft" style={{ animationDelay: '600ms' }}>
              <div className="inline-flex items-center space-x-2 bg-green-50 border border-green-100 px-5 py-2.5 rounded-full">
                <CheckCircle className="h-6 w-6 text-green-500" />
                <span className="text-gray-700 font-medium">350+ Projects Delivered</span>
              </div>
            </div>
          </div>

          <div className="relative animate-fadeInUp" style={{ animationDelay: '300ms' }}>
            <div className="relative">
              {/* Floating Elements Background */}
              <div className="absolute inset-0 animate-pulse">
                <div className="absolute top-0 right-0 w-72 h-72 bg-blue-300 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
                <div className="absolute bottom-0 left-0 w-72 h-72 bg-blue-400 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
                <div className="absolute top-1/2 left-1/2 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>
              </div>

              <div className="relative bg-gradient-to-br from-blue-300   to-black rounded-3xl p-8 shadow-2xl transform hover:scale-105 transition-all duration-500">
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { icon: Code, label: 'Web Development', delay: '0ms' },
                    { icon: Smartphone, label: 'Mobile Apps', delay: '100ms' },
                    { icon: Cloud, label: 'Cloud Solutions', delay: '200ms' },
                    { icon: Search, label: 'SEO Services', delay: '300ms' }
                  ].map((item, index) => (
                    <div
                      key={index}
                      className="bg-white/10 backdrop-blur-md p-6 rounded-xl hover:bg-white/20 transition-all transform hover:scale-110 hover:-translate-y-2 cursor-pointer group"
                      style={{ animation: `scaleIn 0.5s ease-out ${item.delay} both` }}
                    >
                      <item.icon className="h-10 w-10 text-white mb-3 group-hover:scale-125 transition-transform" />
                      <p className="text-white font-semibold text-sm">{item.label}</p>
                    </div>
                  ))}
                </div>

                {/* Floating Stats */}
                <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl shadow-2xl p-6 animate-float">
                  <div className="flex items-center space-x-3">
                    <div className="bg-green-100 p-3 rounded-full">
                      <TrendingUp className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-gray-900">95%</div>
                      <div className="text-sm text-gray-600">Success Rate</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function About() {
  const stats = [
    { number: '35+', label: 'Employees', icon: Users },
    { number: '350+', label: 'Projects Delivered', icon: Target },
    { number: '95%', label: 'Client Satisfaction', icon: Star }
  ];

  return (
    <section id="about" className="py-24 bg-white relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-blue-50/50 to-white"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16 animate-fadeInUp">
          <div className="inline-block mb-4">
            <span className="bg-blue-100 text-blue-900 px-4 py-2 rounded-full text-sm font-semibold">About Us</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            About KS Infotech
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            A leading IT solutions provider dedicated to helping businesses thrive in the digital age.
            With our expert team and proven track record, we deliver excellence in every project.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 md:gap-8 mb-16 max-w-4xl mx-auto">
          {stats.map((stat, index) => (
            <div
              key={index}
              className="group bg-gradient-to-br from-blue-50 to-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all transform hover:scale-110 hover:-translate-y-2 text-center cursor-pointer border border-blue-100 animate-fadeInUp"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className="bg-gradient-to-br from-blue-300  to-black w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform shadow-lg">
                <stat.icon className="h-8 w-8 text-white" />
              </div>
              <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-300  to-black bg-clip-text text-transparent mb-2">
                {stat.number}
              </div>
              <div className="text-base md:text-lg text-gray-700 font-semibold">
                {stat.label}
              </div>
            </div>
          ))}
        </div>

        <div className="bg-gradient-to-br from-blue-300   to-black rounded-3xl p-10 md:p-14 text-white shadow-2xl relative overflow-hidden animate-fadeInUp" style={{ animationDelay: '400ms' }}>
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
              backgroundSize: '40px 40px'
            }}></div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-10 items-center relative z-10">
            <div className="space-y-6">
              <div className="flex items-center space-x-3 mb-4">
                <Target className="h-10 w-10" />
                <h3 className="text-3xl md:text-4xl font-bold">
                  Our Mission
                </h3>
              </div>
              <p className="text-blue-50 leading-relaxed text-lg">
                We are committed to delivering innovative technology solutions that drive business growth
                and digital transformation. Our team of 35+ skilled professionals works tirelessly to
                exceed client expectations and build lasting partnerships.
              </p>
            </div>
            <div className="space-y-6">
              <div className="flex items-center space-x-3 mb-4">
                <Shield className="h-10 w-10" />
                <h3 className="text-3xl md:text-4xl font-bold">
                  Our Vision
                </h3>
              </div>
              <p className="text-blue-50 leading-relaxed text-lg">
                To be the most trusted technology partner for businesses worldwide, recognized for our
                expertise, innovation, and commitment to delivering exceptional results that transform
                the way companies operate in the digital landscape.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Services() {
  const services = [
    {
      icon: Code,
      title: 'Web Development',
      description: 'Custom websites and web applications built with modern frameworks and best practices.',
      color: 'from-blue-300  to-black'
    },
    {
      icon: Smartphone,
      title: 'Mobile App Development',
      description: 'Native and cross-platform mobile applications for iOS and Android devices.',
      color: 'from-blue-300  to-black'
    },
    {
      icon: Search,
      title: 'SEO Services',
      description: 'Comprehensive SEO strategies to improve your online visibility and rankings.',
      color: 'from-blue-300  to-black'
    },
    {
      icon: Cloud,
      title: 'Cloud Solutions',
      description: 'Scalable cloud infrastructure and migration services for your business needs.',
      color: 'from-blue-300  to-black'
    },
    {
      icon: Palette,
      title: 'UI/UX Design',
      description: 'Beautiful, intuitive interfaces that provide exceptional user experiences.',
      color: 'from-blue-300  to-black'
    },
    {
      icon: Target,
      title: 'Digital Marketing',
      description: 'Data-driven marketing strategies to grow your online presence and reach.',
      color: 'from-blue-300  to-black'
    }
  ];

  return (
    <section id="services" className="py-24 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(to right, #3b82f6 1px, transparent 1px), linear-gradient(to bottom, #3b82f6 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16 animate-fadeInUp">
          <div className="inline-block mb-4">
            <span className="bg-blue-100 text-blue-900 px-4 py-2 rounded-full text-sm font-semibold">What We Offer</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Our Services
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Comprehensive technology solutions tailored to your business needs
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <div
              key={index}
              className="group bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-3 cursor-pointer border border-gray-100 animate-fadeInUp"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className={`bg-gradient-to-br ${service.color} w-20 h-20 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all shadow-lg`}>
                <service.icon className="h-10 w-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-blue-900 transition-colors">
                {service.title}
              </h3>
              <p className="text-gray-600 leading-relaxed text-lg">
                {service.description}
              </p>
              <div className="mt-6 flex items-center text-blue-900 font-semibold group-hover:translate-x-2 transition-transform">
                Learn More <ArrowRight className="h-5 w-5 ml-2" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Training() {
  return (
    <section id="training" className="py-24 bg-white relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className="relative animate-fadeInUp">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-300  to-black rounded-3xl blur-2xl opacity-20 animate-pulse"></div>
            <div className="relative bg-gradient-to-br from-blue-300   to-black rounded-3xl p-12 text-white shadow-2xl transform hover:scale-105 transition-all duration-500">
              <div className="space-y-6">
                <div className="bg-white/10 backdrop-blur-sm w-20 h-20 rounded-2xl flex items-center justify-center animate-float">
                  <Award className="h-12 w-12" />
                </div>
                <h3 className="text-4xl font-bold">Online IT Training</h3>
                <p className="text-blue-100 text-xl leading-relaxed">
                  Upskill your team with our comprehensive online training programs
                </p>
                <div className="space-y-4 pt-4">
                  {[
                    'Expert instructors with industry experience',
                    'Flexible learning schedules',
                    'Hands-on practical projects',
                    'Industry-recognized certifications'
                  ].map((item, index) => (
                    <div
                      key={index}
                      className="flex items-center space-x-3 bg-white/10 backdrop-blur-sm p-4 rounded-xl hover:bg-white/20 transition-all transform hover:translate-x-2"
                      style={{ animation: `slideInLeft 0.5s ease-out ${index * 100}ms both` }}
                    >
                      <CheckCircle className="h-7 w-7 flex-shrink-0" />
                      <span className="text-lg">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-8 animate-fadeInUp" style={{ animationDelay: '200ms' }}>
            <div className="inline-block">
              <span className="bg-blue-100 text-blue-900 px-4 py-2 rounded-full text-sm font-semibold">Professional Training</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
              Transform Your Career with Professional IT Training
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              Our online IT training programs are designed to equip professionals with the latest
              skills in web development, cloud computing, cybersecurity, data science, and more.
              Learn from industry experts and gain practical experience through real-world projects.
            </p>
            <div className="grid grid-cols-2 gap-6">
              {[
                { number: '500+', label: 'Students Trained' },
                { number: '50+', label: 'Courses Available' }
              ].map((stat, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-br from-blue-50 to-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all transform hover:scale-105 group cursor-pointer"
                >
                  <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-300  to-black bg-clip-text text-transparent mb-2 group-hover:scale-110 transition-transform">
                    {stat.number}
                  </div>
                  <div className="text-gray-700 font-semibold text-lg">{stat.label}</div>
                </div>
              ))}
            </div>
            <button className="group bg-gradient-to-r from-blue-300  to-black text-white px-8 py-4 rounded-full hover:shadow-2xl transition-all transform hover:scale-105 font-semibold text-lg flex items-center space-x-2 relative overflow-hidden">
              <span className="relative z-10">Explore Training Programs</span>
              <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform relative z-10" />
              <div className="absolute inset-0 bg-gradient-to-r from-blue-300  to-blue-950 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhyChooseUs() {
  const features = [
    {
      icon: Users,
      title: 'Expert Team',
      description: '35+ skilled professionals with diverse expertise and proven track records'
    },
    {
      icon: Zap,
      title: 'Latest Technologies',
      description: 'We leverage cutting-edge tools and frameworks to build future-ready solutions'
    },
    {
      icon: Award,
      title: 'Client-Focused Approach',
      description: 'Your success is our priority. We tailor solutions to meet your unique needs'
    },
    {
      icon: Clock,
      title: '24/7 Support',
      description: 'Round-the-clock technical support to ensure your business runs smoothly'
    }
  ];

  return (
    <section className="py-24 bg-gradient-to-br from-blue-300   to-black text-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '50px 50px'
        }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16 animate-fadeInUp">
          <div className="inline-block mb-4">
            <span className="bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-semibold">Why Us</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Why Choose KS Infotech?
          </h2>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Partner with us for innovative solutions, expert guidance, and unmatched support
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, index) => (
            <div
              key={index}
              className="group bg-white/10 backdrop-blur-md p-8 rounded-2xl hover:bg-white/20 transition-all transform hover:scale-105 hover:-translate-y-2 cursor-pointer border border-white/20 animate-fadeInUp"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className="bg-white/20 backdrop-blur-sm w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-12 transition-all">
                <feature.icon className="h-10 w-10" />
              </div>
              <h3 className="text-2xl font-bold mb-4">{feature.title}</h3>
              <p className="text-blue-100 text-lg leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  const testimonials = [
    {
      name: 'Sarah Johnson',
      company: 'Tech Innovations Inc.',
      text: 'KS Infotech delivered an outstanding web application for our business. Their attention to detail and professional approach exceeded our expectations.',
      rating: 5,
      image: '👩‍💼'
    },
    {
      name: 'Michael Chen',
      company: 'Digital Marketing Pro',
      text: 'The SEO services provided by Ks Infotech significantly improved our online visibility. We saw a 200% increase in organic traffic within 6 months.',
      rating: 5,
      image: '👨‍💼'
    },
    {
      name: 'Emily Rodriguez',
      company: 'StartUp Ventures',
      text: 'Their mobile app development team is exceptional. They built a robust, user-friendly app that our customers love. Highly recommended!',
      rating: 5,
      image: '👩‍💻'
    }
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16 animate-fadeInUp">
          <div className="inline-block mb-4">
            <span className="bg-blue-100 text-blue-900 px-4 py-2 rounded-full text-sm font-semibold">Testimonials</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            What Our Clients Say
          </h2>
          <p className="text-xl text-gray-600">
            Trusted by businesses worldwide
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <div
              key={index}
              className="group bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-2 border border-gray-100 animate-fadeInUp"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className="flex mb-6">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="h-6 w-6 text-yellow-400 fill-current" />
                ))}
              </div>
              <p className="text-gray-600 mb-6 leading-relaxed text-lg italic">
                "{testimonial.text}"
              </p>
              <div className="border-t pt-6 flex items-center space-x-4">
                <div className="bg-gradient-to-br from-blue-300  to-black w-14 h-14 rounded-full flex items-center justify-center text-2xl shadow-lg">
                  {testimonial.image}
                </div>
                <div>
                  <div className="font-bold text-gray-900 text-lg">{testimonial.name}</div>
                  <div className="text-sm text-gray-500">{testimonial.company}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ClientsPartners() {
  const partners = [
    {
      name: "VISION TECH GLOBAL",
      logo: tg,
    },
    {
      name: "VTCodeX",
      logo: vt,
    },
    {
      name: "CMC COUNCIL BOARDS",
      logo: bbs,
    },
    {
      name: "JAIN RICE MEAL PVT LTD",
      logo: ks,
    },
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-4xl font-bold text-center text-gray-900 mb-4 animate-fadeInUp">
          Trusted by Leading Companies
        </h2>

        <p
          className="text-center text-gray-600 mb-12 text-lg animate-fadeInUp"
          style={{ animationDelay: "100ms" }}
        >
          We've partnered with industry leaders to deliver exceptional results
        </p>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8 items-center">
          {partners.map((partner, index) => (
            <div
              key={index}
              className="group bg-gradient-to-br from-gray-200 to-white min-h-40 rounded-2xl flex flex-col items-center justify-center hover:shadow-xl transition-all transform hover:scale-105 border border-gray-200 cursor-pointer animate-fadeInUp px-4 py-6"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Logo Box */}
              <div className="w-20 h-20 mb-4 rounded-xl bg-white shadow-sm flex items-center justify-center p-2 group-hover:shadow-md transition-all">
                <img
                  src={partner.logo}
                  alt={`${partner.name} logo`}
                  className="max-w-full max-h-full object-contain"
                />
              </div>

              {/* Company Name */}
              <div className="text-gray-500 group-hover:text-blue-600 font-bold text-lg text-center transition-colors tracking-wide">
                {partner.name}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// export default ClientsPartners;

// export default ClientsPartners;

function Careers() {
  const positions = [
    'Senior Full Stack Developer',
    'UI/UX Designer',
    'DevOps Engineer',
    'Digital Marketing Specialist',
    'Project Manager'
  ];

  return (
    <section id="careers" className="py-24 bg-gradient-to-br from-blue-50 to-white relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className="animate-fadeInUp">
            <div className="inline-block mb-4">
              <span className="bg-blue-100 text-blue-900 px-4 py-2 rounded-full text-sm font-semibold">Join Our Team</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Join Our Growing Team
            </h2>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              We're always looking for talented individuals to join our team of 35+ professionals.
              If you're passionate about technology and want to work on exciting projects with leading
              companies, we'd love to hear from you.
            </p>
            <div className="space-y-4 mb-8">
              {[
                'Competitive salary and benefits',
                'Flexible work arrangements',
                'Professional development opportunities',
                'Collaborative and inclusive culture'
              ].map((benefit, index) => (
                <div
                  key={index}
                  className="flex items-center space-x-3 bg-white p-4 rounded-xl shadow-md hover:shadow-lg transition-all transform hover:translate-x-2"
                  style={{ animation: `slideInLeft 0.5s ease-out ${index * 100}ms both` }}
                >
                  <CheckCircle className="h-7 w-7 text-blue-900 flex-shrink-0" />
                  <span className="text-gray-700 text-lg font-medium">{benefit}</span>
                </div>
              ))}
            </div>
            <button className="group bg-gradient-to-r from-blue-300  to-black text-white px-8 py-4 rounded-full hover:shadow-2xl transition-all transform hover:scale-105 font-semibold text-lg flex items-center space-x-2 relative overflow-hidden">
              <span className="relative z-10">View Open Positions</span>
              <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform relative z-10" />
              <div className="absolute inset-0 bg-gradient-to-r from-blue-300  to-blue-950 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
            </button>
          </div>

          <div className="relative animate-fadeInUp" style={{ animationDelay: '200ms' }}>
            <div className="absolute inset-0 bg-gradient-to-br from-blue-300  to-black rounded-3xl blur-2xl opacity-20 animate-pulse"></div>
            <div className="relative bg-gradient-to-br from-blue-300   to-black rounded-3xl p-10 text-white shadow-2xl">
              <div className="bg-white/10 backdrop-blur-sm w-20 h-20 rounded-2xl flex items-center justify-center mb-6 animate-float">
                <Briefcase className="h-12 w-12" />
              </div>
              <h3 className="text-3xl font-bold mb-6">Current Openings</h3>
              <div className="space-y-4">
                {positions.map((position, index) => (
                  <div
                    key={index}
                    className="group bg-white/10 backdrop-blur-sm p-5 rounded-xl hover:bg-white/20 transition-all cursor-pointer transform hover:translate-x-2 border border-white/20"
                    style={{ animation: `slideInRight 0.5s ease-out ${index * 100}ms both` }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="font-semibold text-lg">{position}</div>
                      <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Contact() {
  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16 animate-fadeInUp">
          <div className="inline-block mb-4">
            <span className="bg-blue-100 text-blue-900 px-4 py-2 rounded-full text-sm font-semibold">Contact Us</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Get In Touch
          </h2>
          <p className="text-xl text-gray-600">
            Let's discuss how we can help transform your business
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          <div className="space-y-8 animate-fadeInUp">
            <div className="bg-gradient-to-br from-blue-300   to-black rounded-3xl p-10 text-white shadow-2xl relative overflow-hidden">
              <div className="absolute inset-0 opacity-10">
                <div className="absolute inset-0" style={{
                  backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
                  backgroundSize: '40px 40px'
                }}></div>
              </div>
              
              <h3 className="text-3xl font-bold mb-8 relative z-10">Contact Information</h3>
              <div className="space-y-6 relative z-10">
                {[
                  {
                    icon: MapPin,
                    title: 'Address',
                    content: (
                      <>
                        504/24 Arya nagar,<br />
                        near R.D. public school<br />
                        Hansi Haryana, 125033
                      </>
                    )
                  },
                  {
                    icon: Phone,
                    title: 'Phone',
                    content: '+91 93504-96575'
                  },
                  {
                    icon: Mail,
                    title: 'Email',
                    content: 'ks@infotechks.com'
                  }
                ].map((item, index) => (
                  <div
                    key={index}
                    className="flex items-start space-x-4 bg-white/10 backdrop-blur-sm p-6 rounded-2xl hover:bg-white/20 transition-all transform hover:translate-x-2"
                    style={{ animation: `slideInLeft 0.5s ease-out ${index * 100}ms both` }}
                  >
                    <div className="bg-white/20 backdrop-blur-sm p-3 rounded-xl">
                      <item.icon className="h-7 w-7 flex-shrink-0" />
                    </div>
                    <div>
                      <div className="font-bold mb-2 text-lg">{item.title}</div>
                      <div className="text-blue-100 text-base leading-relaxed">
                        {item.content}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-white rounded-3xl p-8 shadow-lg border border-blue-100 animate-fadeInUp" style={{ animationDelay: '300ms' }}>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Business Hours</h3>
              <div className="space-y-3">
                {[
                  { day: 'Monday - Friday:', time: '9:00 AM - 6:00 PM' },
                  { day: 'Saturday:', time: '10:00 AM - 4:00 PM' },
                  { day: 'Sunday:', time: 'Closed' }
                ].map((item, index) => (
                  <div key={index} className="flex justify-between items-center p-4 bg-white rounded-xl hover:shadow-md transition-all">
                    <span className="text-gray-700 font-medium">{item.day}</span>
                    <span className="text-blue-900 font-bold">{item.time}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="animate-fadeInUp" style={{ animationDelay: '200ms' }}>
            <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-4 shadow-xl border border-gray-200 h-full min-h-[600px]">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3486.299348353443!2d75.95163691149953!3d29.096842562741383!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x391239046dc7bddb%3A0x2d7fff6286e7a51f!2sRd%20Public%20School%20Hansi%20%2Charayana!5e0!3m2!1sen!2sin!4v1770643035562!5m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0, borderRadius: '20px', minHeight: '580px' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="KS Infotech Location"
                className="shadow-lg"
              ></iframe>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer({ scrollToSection }) {
  return (
    <footer className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-16 relative overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid md:grid-cols-4 gap-12 mb-12">
          <div className="animate-fadeInUp">
            <div className="flex items-center space-x-3 mb-6">
              <div className="bg-gradient-to-br from-blue-300  to-black p-3 rounded-xl shadow-lg">
                <Code className="h-8 w-8 text-white" />
              </div>
              <span className="text-2xl font-bold">KS INFOTECH</span>
            </div>
            <p className="text-gray-400 leading-relaxed text-lg">
              Transforming businesses through innovative technology solutions.
            </p>  
            <div className="flex space-x-4 mt-6">
              {['facebook', 'twitter', 'linkedin', 'instagram'].map((social, index) => (
                <div
                  key={social}
                  className="bg-white/10 backdrop-blur-sm w-12 h-12 rounded-full flex items-center justify-center hover:bg-gradient-to-br hover:from-blue-300  hover:to-black transition-all cursor-pointer transform hover:scale-110"
                  style={{ animation: `scaleIn 0.5s ease-out ${index * 100}ms both` }}
                >
                  <span className="text-sm font-bold">{social[0].toUpperCase()}</span>
                </div>
              ))}
            </div>
          </div>

          {[
            {
              title: 'Quick Links',
              links: ['About Us', 'Services', 'Training', 'Branches', 'Careers']
            },
            {
              title: 'Services',
              links: ['Web Development', 'Mobile Apps', 'SEO Services', 'Cloud Solutions']
            },
            {
              title: 'Contact',
              links: ['ks@infotechks.com', '504/24 Arya nagar', 'Hansi Haryana', '125033']
            }
          ].map((column, colIndex) => (
            <div key={colIndex} className="animate-fadeInUp" style={{ animationDelay: `${(colIndex + 1) * 100}ms` }}>
              <h4 className="font-bold text-xl mb-6">{column.title}</h4>
              <div className="space-y-3">
                {column.links.map((link, index) => (
                  <button
                    key={index}
                    onClick={() => column.title === 'Quick Links' && scrollToSection(link.toLowerCase().replace(' ', ''))}
                    className="block text-gray-400 hover:text-white hover:translate-x-2 transition-all text-left"
                  >
                    {link}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="border-t border-gray-700 pt-8 animate-fadeInUp" style={{ animationDelay: '400ms' }}>
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-400 text-center md:text-left">
              &copy; 2025 KS INFOTECH. All rights reserved.
            </p>
            <div className="flex space-x-6 text-gray-400">
              <button className="hover:text-white transition-colors">Privacy Policy</button>
              <button className="hover:text-white transition-colors">Terms of Service</button>
            </div>
          </div>
          
          {/* Designed and Developed by Section */}
          <div className="mt-6 text-center animate-fadeInUp" style={{ animationDelay: '500ms' }}>
            <p className="text-gray-400 text-sm">
              Designed and Developed by{' '}
              <a
                href="https://www.linkedin.com/in/tanisha-garg-70116b313/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-300 hover:text-blue-200 font-semibold transition-all hover:underline decoration-2 underline-offset-4 inline-flex items-center space-x-1 group"
              >
                <span>Tanisha</span>
                <svg
                  className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                </svg>
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default App;

<style jsx global>{`
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes slideInLeft {
    from {
      opacity: 0;
      transform: translateX(-30px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @keyframes slideInRight {
    from {
      opacity: 0;
      transform: translateX(30px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @keyframes scaleIn {
    from {
      opacity: 0;
      transform: scale(0.8);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  @keyframes float {
    0%, 100% {
      transform: translateY(0px);
    }
    50% {
      transform: translateY(-20px);
    }
  }

  @keyframes blob {
    0%, 100% {
      transform: translate(0px, 0px) scale(1);
    }
    33% {
      transform: translate(30px, -50px) scale(1.1);
    }
    66% {
      transform: translate(-20px, 20px) scale(0.9);
    }
  }

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateX(-20px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  .animate-fadeInUp {
    animation: fadeInUp 0.6s ease-out both;
  }

  .animate-slideInLeft {
    animation: slideInLeft 0.6s ease-out both;
  }

  .animate-slideInRight {
    animation: slideInRight 0.6s ease-out both;
  }

  .animate-scaleIn {
    animation: scaleIn 0.6s ease-out both;
  }

  .animate-float {
    animation: float 3s ease-in-out infinite;
  }

  .animate-blob {
    animation: blob 7s infinite;
  }

  .animation-delay-2000 {
    animation-delay: 2s;
  }

  .animation-delay-4000 {
    animation-delay: 4s;
  }

  html {
    scroll-behavior: smooth;
  }

  * {
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
`}</style>